import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';

export type Theme = 'dark' | 'light';
type CurtainPhase = 'in' | 'out' | null;

const STORAGE_KEY = 'ecomflow_theme';

// Kurtyna — czasy per faza. Łącznie 900ms (450 in + 450 out).
const CURTAIN_IN_MS = 450;
const CURTAIN_OUT_MS = 450;

// Kolory kurtyny per motyw — surface-base każdego trybu. Kurtyna ma kolor
// motywu DOCELOWEGO, żeby moment "kurtyna znika, odsłania nowy motyw" był
// seamless (ten sam kolor pod spodem).
const CURTAIN_BG: Record<Theme, string> = {
  dark: '#1e1e26',
  light: '#f4f6fa',
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'dark' || stored === 'light' ? stored : null;
}

function detectInitialTheme(): Theme {
  // Anti-flash inline script w index.html już ustawił klasę — jeśli jest,
  // zsynchronizuj się z nią zamiast re-liczyć (uniknij skoku przy hydracji).
  if (typeof document !== 'undefined') {
    if (document.documentElement.classList.contains('light')) return 'light';
    if (document.documentElement.classList.contains('dark')) return 'dark';
  }
  const stored = readStoredTheme();
  if (stored) return stored;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(detectInitialTheme);
  const [curtainPhase, setCurtainPhase] = useState<CurtainPhase>(null);
  const [curtainColor, setCurtainColor] = useState<string>(CURTAIN_BG[theme]);
  const isFirstRender = useRef(true);
  const timersRef = useRef<number[]>([]);

  // Aplikacja klasy na <html> przy zmianie motywu.
  // Pierwszy render → anti-flash script już ustawił klasę, tylko się zsynchronizuj.
  // Kolejne → klasa swapowana jest przez kurtynę, nie w tym useEffect.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const root = document.documentElement;
      root.classList.remove('dark', 'light');
      root.classList.add(theme);
    }
  }, [theme]);

  // Cleanup wszystkich timerów przy unmount (edge case — user nawiguje gdzieś
  // w trakcie animacji, context się demontuje).
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  const applyTheme = (newTheme: Theme) => {
    // Jeśli ta sama wartość albo pierwszy render — zrób prosto, bez kurtyny.
    if (newTheme === theme || isFirstRender.current) {
      setThemeState(newTheme);
      return;
    }

    // Clear poprzednie timery jeśli user szybko klika w trakcie animacji.
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    // Kurtyna ma kolor DOCELOWEGO motywu — po odsłonięciu seamless.
    setCurtainColor(CURTAIN_BG[newTheme]);

    // Faza IN: kurtyna zjeżdża od góry, zakrywa ekran w CURTAIN_IN_MS.
    setCurtainPhase('in');

    const t1 = window.setTimeout(() => {
      // Pod kurtyną: swap class na <html> + state + localStorage. Wszystkie
      // kolory zmieniają się instant, niewidoczne przez użytkownika.
      const root = document.documentElement;
      root.classList.remove('dark', 'light');
      root.classList.add(newTheme);
      setThemeState(newTheme);
      try {
        window.localStorage.setItem(STORAGE_KEY, newTheme);
      } catch {
        // localStorage niedostępny — ignoruj, tryb per sesja
      }

      // Faza OUT: kurtyna jedzie z powrotem do góry, odsłania nowy motyw.
      setCurtainPhase('out');

      const t2 = window.setTimeout(() => {
        setCurtainPhase(null);
      }, CURTAIN_OUT_MS);
      timersRef.current.push(t2);
    }, CURTAIN_IN_MS);
    timersRef.current.push(t1);
  };

  // Reaguj na systemową zmianę tylko gdy user nie wybrał nic ręcznie.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      if (readStoredTheme() === null) {
        applyTheme(e.matches ? 'light' : 'dark');
      }
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: ThemeContextValue = {
    theme,
    setTheme: applyTheme,
    toggleTheme: () => applyTheme(theme === 'dark' ? 'light' : 'dark'),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
      {curtainPhase && (
        <div
          className="theme-curtain"
          data-phase={curtainPhase}
          style={{ background: curtainColor }}
          aria-hidden="true"
        />
      )}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
